import React, { type FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { SetCompanyAlarmDialogVisibilityAction } from '../../../redux/meter-table/meter-table-actions'
import { UpdateCompanyAlarmAction } from '../../../redux/companies/companies-actions'
import { SetCompanyAlarmForm } from './set-company-alarm-form/SetCompanyAlarmForm'
import { SetCompanyAlarmConfirmDialog } from './set-company-alarm-confirm-dialog/SetCompanyAlarmConfirmDialog'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type RootState } from '../../../redux/store'
import { type OwnCompany } from '../../../redux/auth/auth-types'
import { type AdminCompany } from '../../../redux/admin/admin-companies-types'

export const SetCompanyAlarmDialog: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const isAdmin = useAppSelector((state: RootState) => state.auth.isAdmin)
  const isOpen = useAppSelector((state: RootState) => state.meterTable.isSetCompanyAlarmDialogOpen)
  const companyAlarm = useAppSelector((state: RootState) => state.meterTable.companyAlarm)
  const adminCompanies = useAppSelector<AdminCompany[]>(state => state.admin.companies)
  const ownCompany = useAppSelector<OwnCompany | null>(state => state.auth.ownCompany)

  const companies = useMemo(() => {
    if (isAdmin) {
      return adminCompanies.map(company => ({ id: company.id, name: company.name }))
    }
    if (ownCompany != null) {
      return [{ id: ownCompany.id, name: ownCompany.name }]
    }
    return []
  }, [adminCompanies, isAdmin, ownCompany])

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false)

  const handleClose = (): void => {
    dispatch(SetCompanyAlarmDialogVisibilityAction(false))
    setIsConfirmDialogOpen(false)
  }

  const handleSubmit = (): void => {
    setIsConfirmDialogOpen(true)
  }

  const handleConfirm = (): void => {
    dispatch(UpdateCompanyAlarmAction({ companyAlarm }))
    handleClose()
  }

  const handleConfirmDialogClose = (): void => {
    setIsConfirmDialogOpen(false)
  }

  return (
        <>
            <Dialog
                onClose={handleClose}
                open={isOpen}
                data-testid="set-company-alarm-dialog"
                maxWidth="sm"
            >
                <DialogTitle>
                    {t('company_alarm_dialog.edit_all_alarms')}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <SetCompanyAlarmForm
                        companies={companies}
                        companyAlarm={companyAlarm}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        {t('common.actions.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} color="primary">
                        {t('common.actions.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <SetCompanyAlarmConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={handleConfirmDialogClose}
                onConfirm={handleConfirm}
            />
        </>
  )
}
